/* TODO: t() */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Button, Hoverable, Picker, Text, ColorPicker, Modal } from '../uiold';

import {
  WHITE_RGB_COLOR,
  BLACK_RGB_COLOR,
  themeColors,
} from '../uiold/Constants';
import { HoverFieldType } from './types';
import {
  Account,
  RGBColor,
  VendorProfileTemplateType,
  Settings,
} from '../../types/shared/Account';
import { Season } from '../../types/shared/Season';
import SignupFormModel from '../FormBuilder/SignupFormModel';
import IndexedList from '../../support/shared/IndexedList';
import { VendorSummary } from '../../types/shared/Vendor';
import AccountActions from '../../support/AccountActions';
import { postJSON } from '../../support/Fetch';
import { showSuccess } from '../App/AppAlert';
import CardBase from './Cards/CardBase';
import { FieldDef } from '../../types/shared/FormFieldDefinition';
import { CardThumbnails } from './Cards/CardTemplates';

import getColorString from '../../support/helpers/getColorString';
import { DEFAULT_VENDOR_PROFILE_TEMPLATE } from '../../support/shared/constants/Constants';

type Props = {
  onHide: () => void;
  isModalVisible: boolean;
  account: Account | undefined;
  season?: Season | undefined;
  formDef: SignupFormModel | null | undefined;
  vendors: IndexedList<VendorSummary> | null | undefined;
};

function idToField(fields: Array<FieldDef>, id?: string) {
  if (id) {
    return fields.find((field) => field.id === id);
  }
  return;
}

export default function ProfileTemplateDialog(props: Props) {
  let { onHide, isModalVisible, account, vendors, formDef } = props;
  let fieldDefs = formDef?.fields
    .mapToArray<FieldDef>((field) => field.definition)
    .filter((field) => !field.hidden);
  let fileFields =
    fieldDefs?.filter((fieldDef) => fieldDef.type === 'FileUploadElement') ??
    [];
  let textFields =
    fieldDefs?.filter((fieldDef) => fieldDef.type === 'TextElement') ?? [];
  let vendorProfileTemplate =
    account?.settings?.vendorProfileTemplate ?? DEFAULT_VENDOR_PROFILE_TEMPLATE;
  let [
    selectedCardOptionType,
    setSelectedCardOptionType,
  ] = useState<VendorProfileTemplateType>(
    vendorProfileTemplate?.type ?? 'horizontalCardSquare',
  );
  let [selectedVendorExample, setSelectedVendorExample] = useState<
    VendorSummary | undefined
  >(vendors?.list[0]);
  let [vendorImageField, setVendorImageField] = useState(
    idToField(fileFields, vendorProfileTemplate?.imageID ?? fileFields[0]?.id),
  );
  let [vendorNameField, setVendorNameField] = useState(
    idToField(textFields, vendorProfileTemplate?.nameID ?? textFields[0]?.id),
  );
  let [vendorDescriptionField, setVendorDescriptionField] = useState(
    idToField(
      textFields,
      vendorProfileTemplate?.descriptionID ?? textFields[0]?.id,
    ),
  );
  let [hoverFieldType, setHoverFieldType] = useState<HoverFieldType | null>(
    null,
  );
  let [nameColor, setNameColor] = useState<RGBColor>(
    vendorProfileTemplate?.nameColor ?? BLACK_RGB_COLOR,
  );
  let [descriptionColor, setDescriptionColor] = useState<RGBColor>(
    vendorProfileTemplate?.descriptionColor ?? BLACK_RGB_COLOR,
  );
  let [backgroundColor, setBackgroundColor] = useState<RGBColor>(
    vendorProfileTemplate?.backgroundColor ?? WHITE_RGB_COLOR,
  );

  let onNameColorChange = (color: RGBColor) => {
    setNameColor(color);
  };
  let onDescriptionColorChange = (color: RGBColor) => {
    setDescriptionColor(color);
  };
  let onBackgroundColorChange = (color: RGBColor) => {
    setBackgroundColor(color);
  };

  useEffect(() => {
    if (vendors) {
      setSelectedVendorExample(vendors?.list[0]);
    }
  }, [vendors]);

  if (!account || !vendors) {
    return null;
  }

  let onSave = () => {
    if (account) {
      let oldSettings = account?.settings;
      let newSettings: Settings = {
        ...oldSettings,
        vendorProfileTemplate: {
          type: selectedCardOptionType,
          imageID: vendorImageField?.id,
          nameID: vendorNameField?.id,
          descriptionID: vendorDescriptionField?.id,
          nameColor,
          descriptionColor,
          backgroundColor,
        },
      };
      AccountActions.updateAccount({ settings: newSettings });
      // TODO: Do we need to deal with season here?
      postJSON(`/api/accounts/${account.id}/embedSettings`, newSettings);
      showSuccess('Vender Profile Template settings saved.');
      onHide();
    }
  };

  return (
    <Modal
      isVisible={isModalVisible}
      onClose={onHide}
      style={{ width: 840 }}
      scrollBehavior="outside"
    >
      <Modal.Header title="Vendor Profile Template" />
      <Modal.Body>
        <View style={styles.modalContent}>
          <View style={styles.columnDirection}>
            {CardThumbnails.map((cardThumbnail) => {
              return (
                <TouchableOpacity
                  activeOpacity={0.6}
                  key={cardThumbnail.type}
                  onPress={() => setSelectedCardOptionType(cardThumbnail.type)}
                  style={[
                    styles.templateOptionItem,
                    cardThumbnail.type === selectedCardOptionType &&
                      styles.selectedTemplateOptionItem,
                  ]}
                >
                  <Text>{cardThumbnail.renderImage()}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <View style={styles.previewContainer}>
            {selectedCardOptionType ? (
              <CardBase
                vendorID={selectedVendorExample?.id ?? ''}
                selectedImageID={vendorImageField?.id ?? ''}
                selectedNameID={vendorNameField?.id ?? ''}
                selectedDescriptionID={vendorDescriptionField?.id ?? ''}
                accountID={account.id}
                hoverFieldType={hoverFieldType}
                selectedCardOptionType={selectedCardOptionType}
                nameColor={getColorString(nameColor)}
                descriptionColor={getColorString(descriptionColor)}
                backgroundColor={getColorString(backgroundColor)}
              />
            ) : (
              <Text>You have no Vendors to be displayed.</Text>
            )}
          </View>
          <View style={[styles.columnDirection, styles.flexGrow]}>
            <Hoverable
              render={(isHovered, eventHandlers) => {
                if (isHovered) {
                  setHoverFieldType('card');
                } else if (!isHovered && hoverFieldType === 'card') {
                  setHoverFieldType(null);
                }
                return (
                  <View {...eventHandlers}>
                    <Text style={styles.pickerLabel}>Displayed Vendor</Text>
                  </View>
                );
              }}
            />
            <Picker
              options={vendors?.list ?? []}
              selectedItem={selectedVendorExample}
              onChange={(value) => {
                setSelectedVendorExample(value);
              }}
              width={240}
              style={{
                marginTop: 3,
                marginBottom: 3,
                marginRight: 10,
              }}
            />
            <Hoverable
              render={(isHovered, eventHandlers) => {
                if (isHovered) {
                  setHoverFieldType('image');
                } else if (!isHovered && hoverFieldType === 'image') {
                  setHoverFieldType(null);
                }
                return (
                  <View {...eventHandlers}>
                    <Text style={styles.pickerLabel}>Select Vendor Image</Text>
                  </View>
                );
              }}
            />
            <Picker
              options={fileFields}
              selectedItem={vendorImageField}
              onChange={(value: FieldDef) => {
                setVendorImageField(value);
              }}
              width={240}
              style={{
                marginTop: 3,
                marginBottom: 3,
                marginRight: 10,
              }}
            />
            <Hoverable
              render={(isHovered, eventHandlers) => {
                if (isHovered) {
                  setHoverFieldType('name');
                } else if (!isHovered && hoverFieldType === 'name') {
                  setHoverFieldType(null);
                }
                return (
                  <View {...eventHandlers}>
                    <Text style={styles.pickerLabel}>Select Vendor Name</Text>
                  </View>
                );
              }}
            />
            <View
              style={[styles.rowDirection, styles.pickerGroup, { zIndex: 2 }]}
            >
              <Picker
                options={textFields}
                selectedItem={vendorNameField}
                onChange={(value: FieldDef) => {
                  setVendorNameField(value);
                }}
                width={240}
                style={{
                  marginTop: 3,
                  marginBottom: 3,
                  marginRight: 10,
                }}
              />
              <ColorPicker
                selectedColor={nameColor}
                handleChange={onNameColorChange}
              />
            </View>
            <Hoverable
              render={(isHovered, eventHandlers) => {
                if (isHovered) {
                  setHoverFieldType('description');
                } else if (!isHovered && hoverFieldType === 'description') {
                  setHoverFieldType(null);
                }
                return (
                  <View {...eventHandlers}>
                    <Text style={styles.pickerLabel}>
                      Select Vendor Description
                    </Text>
                  </View>
                );
              }}
            />

            <View style={[styles.rowDirection, styles.pickerGroup]}>
              <Picker
                options={textFields}
                selectedItem={vendorDescriptionField}
                onChange={(value: FieldDef) => setVendorDescriptionField(value)}
                width={240}
                style={{
                  marginTop: 3,
                  marginBottom: 3,
                  marginRight: 10,
                }}
              />
              <ColorPicker
                selectedColor={descriptionColor}
                handleChange={onDescriptionColorChange}
              />
            </View>
            <View
              style={[
                styles.rowDirection,
                styles.backgroundGroup,
                { zIndex: -1 },
              ]}
            >
              <Text style={styles.colorLabel}>Background Color</Text>
              <ColorPicker
                selectedColor={backgroundColor}
                handleChange={onBackgroundColorChange}
              />
            </View>
          </View>
        </View>
      </Modal.Body>

      <Modal.Footer>
        <Button title="Cancel" onPress={onHide} />
        <Button title="Save" onPress={onSave} isPrimary />
      </Modal.Footer>
    </Modal>
  );
}

let styles = StyleSheet.create({
  modalContent: {
    flexDirection: 'row',
    flex: 1,
  },
  rowDirection: {
    flexDirection: 'row',
  },
  columnDirection: {
    flexDirection: 'column',
    position: 'relative',
  },
  templateOptionItem: {
    outlineWidth: 0,
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 5,
    width: 72,
    height: 72,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
    borderRadius: 5,
    backgroundColor: themeColors.white,
    cursor: 'pointer',
  },
  selectedTemplateOptionItem: {
    borderRadius: 5,
    borderColor: themeColors.withOpacity.blue50,
    backgroundColor: themeColors.withOpacity.blue8,
  },
  previewContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 5,
    marginHorizontal: 17,
    width: 380,
    height: 360,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
    borderRadius: 5,
    backgroundColor: themeColors.white,
  },
  pickerGroup: {
    alignItems: 'center',
  },
  pickerLabel: {
    marginLeft: 5,
    marginTop: 11,
  },

  flexGrow: {
    flexGrow: 1,
  },
  backgroundGroup: {
    marginTop: 36,
    marginLeft: 99,
    alignItems: 'center',
  },
  colorLabel: {
    marginRight: 20,
  },
});
